import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, ShieldCheck } from "lucide-react";
import api, { errText } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { useAuth } from "@/context/AuthContext";

export default function Invite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get(`/auth/invites/preview/${token}`)
      .then(({ data }) => {
        setInvite(data);
        if (data.invited_email) setForm((f) => ({ ...f, email: data.invited_email }));
      })
      .catch((err) => setError(errText(err)));
  }, [token]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/invites/accept/${token}`, form);
      setSession(data.token, data.user);
      toast.success(`Welcome, ${data.user.name}`);
      navigate(data.user.role === "staff" ? "/moderator/dashboard" : "/moderator/dashboard");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  if (error)
    return (
      <Shell bare>
        <div className="max-w-md" data-testid="invite-error">
          <h1 className="font-display text-3xl font-black uppercase">Invite unavailable</h1>
          <p className="text-sm text-slate-400 mt-3">{error}</p>
          <p className="text-sm text-slate-500 mt-2">Ask the organiser for a fresh link.</p>
        </div>
      </Shell>
    );

  if (!invite)
    return (
      <Shell bare>
        <p className="text-sm text-slate-500">Checking your invite…</p>
      </Shell>
    );

  return (
    <Shell bare>
      <form onSubmit={submit} className="card p-7 max-w-md" data-testid="invite-accept-form">
        <p className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-widest text-[#00e599]">
          <ShieldCheck size={14} /> Invitation
        </p>
        <h1 className="font-display text-3xl font-black uppercase mt-3">Join the team</h1>
        <p className="text-sm text-slate-400 mt-3" data-testid="invite-role-line">
          {invite.invited_by ? `${invite.invited_by} invited you` : "You have been invited"} as{" "}
          <span className="text-[#00e599]">{invite.role_label}</span>.
        </p>

        <div className="mt-6 space-y-4">
          <div>
            <p className="label">Your name</p>
            <input required className="input" data-testid="invite-name" value={form.name} onChange={set("name")} />
          </div>
          <div>
            <p className="label">Email</p>
            <input required type="email" className="input" data-testid="invite-email"
              value={form.email} onChange={set("email")} />
          </div>
          <div>
            <p className="label">Choose a password</p>
            <input required type="password" minLength={8} className="input" data-testid="invite-password"
              value={form.password} onChange={set("password")} />
            <p className="text-[11px] text-slate-500 mt-1.5">At least 8 characters.</p>
          </div>
        </div>

        <button className="btn-primary mt-7 w-full justify-center" disabled={busy}
          data-testid="invite-accept-btn">
          <UserPlus size={15} /> {busy ? "Creating your account…" : "Create my account"}
        </button>
      </form>
    </Shell>
  );
}
